// combatpro/views/LairView.js
//
// Stage 33: view-lair shell. Stage 71 (Lair Actions View Foundation) migrates the
// legacy per-monster Lair Actions & Environment card (battletracker.html lair-block,
// 6166-6219, renderer refreshLairActions 9319, add/use/remove/toggle 9300-9371,
// environment effects updateEnvironmentEffects 9373) for the ACTIVE monster.
//
// The legacy built one lair-block-${i} per monster inside createMonsterPanel and
// reparented the active one into #vi-lair-mount via vi_mountLair (10263). React renders
// the active monster's card directly from state.currentCombatantIndex (the established
// active-monster view pattern) - no reparenting, no getElementById.
//
// Faithful migration (master spec preserved verbatim):
//   - the header (🏰 Lair Actions & Environment) with the Active checkbox
//     (lairEnabled-${index} -> setMonsterLairEnabled, reflecting monster.lairEnabled)
//     and four static help paragraphs (6168-6184).
//   - the static info line (6186-6190).
//   - the lair list (refreshLairActions 9319): empty -> "No lair actions"; else
//     vi-lair-card entries with "🏰 name", a Recharge badge when recharge is set, a
//     USED badge when used, the description, a "⚡ Use" button (disabled when used) and
//     a Remove button.
//   - the "+ Add Lair Action" workflow (addLairAction 9300): there is NO lair database
//     and NO picker (confirmed by discovery) - Add is always the three-prompt custom
//     path (name required, description, recharge), pushing {name,description,recharge,
//     used:false}.
//   - the Environment Conditions panel (6194-6216): four condition checkboxes
//     (darkness / dimLight / difficultTerrain / hazard) -> toggleMonsterEnvironment,
//     reflecting monster.environment[key], plus the derived effects text
//     (updateEnvironmentEffects 9373-9385) rendered declaratively from the booleans.
//
// The legacy Lair section carries the section-collapsible class but the lair-specific
// CSS (1481-1484) disables the chevron and forces it always-open (cursor:default,
// max-height:none, ::after display:none), and the header has no toggle handler. The
// classes are rendered faithfully but NO collapse behaviour is wired (the section is
// effectively always expanded).
//
// OWNERSHIP (determined from discovery, not assumed): lairActions (each entry's used /
// recharge), lairEnabled, and environment{} are persistent MONSTER fields (monsterEngine
// factory L65-67), not encounter state. Use / Active / Environment / Add / Remove are
// self-contained monster-data mutations, fully migrated here through the Stage 71
// CombatContext handlers. NOT migrated (owned by the encounter / initiative runtime, or
// deferred): the Initiative-20 cross trigger and its encounter-turn used-reset
// (5499-5554) and the addCombatLog side effect on Use. There is no lair database, so no
// readLairDb, no picker, and no graceful-degradation path exist. View-local state is not
// required (no picker / no transient UI). Custom-action entry keeps the legacy
// window.prompt interaction; removal keeps window.confirm. No DOM manipulation, no
// getElementById, no createElement, no innerHTML, no new runtime, no CombatContext
// ownership change. No optional chaining. No em dash.

import React, { useContext } from 'react';
import { CombatContext } from '../contexts/CombatContext';

// Environment effect descriptions (updateEnvironmentEffects 9380-9383), keyed and
// ordered exactly as the legacy checkbox grid.
const ENVIRONMENT_OPTIONS = [
  { key: 'darkness', label: '\ud83c\udf11 Darkness', effect: '\ud83c\udf11 Darkness: Heavily obscured. Attacks have disadvantage unless attacker has darkvision/blindsight.' },
  { key: 'dimLight', label: '\ud83c\udf13 Dim Light', effect: '\ud83c\udf13 Dim Light: Lightly obscured. Disadvantage on Perception checks relying on sight.' },
  { key: 'difficultTerrain', label: '\ud83c\udfd4\ufe0f Difficult Terrain', effect: '\ud83c\udfd4\ufe0f Difficult Terrain: Every foot of movement costs 1 extra foot.' },
  { key: 'hazard', label: '\u26a0\ufe0f Hazard Zone', effect: '\u26a0\ufe0f Hazard Zone: Active environmental hazard in this area.' }
];

function LairView() {
  const {
    state,
    addMonsterLairAction,
    removeMonsterLairAction,
    useMonsterLairAction,
    setMonsterLairEnabled,
    toggleMonsterEnvironment
  } = useContext(CombatContext);
  const index = state.currentCombatantIndex;
  const monster = state.monsters[index];

  if (!monster) {
    return (
      <div className="vi-view" id="view-lair">
        <div id="vi-lair-mount"></div>
      </div>
    );
  }

  const lairActions = Array.isArray(monster.lairActions) ? monster.lairActions : [];
  const lairEnabled = !!monster.lairEnabled;
  const environment = (monster.environment && typeof monster.environment === 'object') ? monster.environment : {};

  // addLairAction (9300): three prompts; name required.
  function onAdd() {
    var name = (typeof window !== 'undefined' && window.prompt) ? window.prompt('Lair Action name:') : null;
    if (!name || !name.trim()) { return; }
    var description = (typeof window !== 'undefined' && window.prompt) ? window.prompt('Description:') : null;
    var recharge = (typeof window !== 'undefined' && window.prompt) ? window.prompt("Recharge (e.g., '5-6' for recharge 5-6, or leave blank for no recharge):") : null;
    var lairAction = {
      name: name.trim(),
      description: description || '',
      recharge: recharge || '',
      used: false
    };
    addMonsterLairAction(index, lairAction);
  }

  function onUse(actionIndex) {
    // useLairAction state write (9346). The legacy addCombatLog (9351) is a deferred
    // side effect owned elsewhere, not migrated here.
    // useMonsterLairAction is a CombatContext action (a setState closure), not a
    // React hook; the 'use' prefix triggers a false positive here.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useMonsterLairAction(index, actionIndex);
  }

  function onRemove(actionIndex) {
    if (typeof window !== 'undefined' && window.confirm && !window.confirm('Remove this lair action?')) { return; }
    removeMonsterLairAction(index, actionIndex);
  }

  function onToggleActive(e) {
    setMonsterLairEnabled(index, e.target.checked);
  }

  function onToggleEnvironment(key, checked) {
    toggleMonsterEnvironment(index, key, checked);
  }

  var activeEffects = ENVIRONMENT_OPTIONS.filter(function(opt) { return !!environment[opt.key]; });

  return (
    <div className="vi-view" id="view-lair">
      <div id="vi-lair-mount">
        <div id={'lair-block-' + index}>
          <div className="section-collapsible" id={'lair-section-' + index}>
            <div className="vi-lair-header">
              <div className="section-header vi-section-header">
                <h3>🏰 Lair Actions & Environment</h3>
                <div className="vi-flex-center vi-gap-10">
                  <label className="vi-lair-active-label">
                    <input type="checkbox" id={'lairEnabled-' + index} onChange={onToggleActive} checked={lairEnabled} />
                    <span>Active</span>
                  </label>
                </div>
              </div>
              <div className="vi-lair-help-text">
                <p>Lair Actions and Environment define how a creature influences the battlefield beyond its standard abilities. These mechanics represent environmental control, regional effects, and passive or triggered events that shape combat flow.</p>
                <p>Use this section to configure lair-based actions, environmental hazards, and ongoing effects tied to the encounter space. These elements typically activate on initiative counts, at the start or end of rounds, or in response to player actions.</p>
                <p>Lair Actions allow powerful creatures to act outside their normal turn cycle, while environmental settings can introduce terrain hazards, visibility changes, or persistent conditions that impact both players and enemies.</p>
                <p>Adjust these settings to control encounter difficulty, pacing, and tactical complexity. Well-configured lair and environment effects create more dynamic and memorable combat scenarios.</p>
              </div>
            </div>
            <div className="section-content vi-section-body">
              <div className="vi-lair-info">
                <div className="vi-lair-info-text">
                  <strong className="vi-text-gold">ℹ️ Lair Actions:</strong> <span className="vi-text-readable">Trigger at Initiative 20 each round. Check Active {'\u2192'} actions auto-trigger. Click + Add Lair Action {'\u2192'} enter name, description, and recharge. Click {'\u26a1'} Use to activate.</span>
                </div>
              </div>

              <div id={'lair-list-' + index} className="vi-mb-10">
                {lairActions.length === 0 ? (
                  <p className="vi-modal-result-text">No lair actions</p>
                ) : (
                  lairActions.map(function(action, i) {
                    return (
                      <div className="vi-lair-card" key={i}>
                        <div className="vi-lair-inner">
                          <div className="vi-flex-1">
                            <div className="vi-lair-name-row">
                              {'\ud83c\udff0 ' + action.name}
                              {action.recharge ? <span className="vi-lair-recharge">{'Recharge ' + action.recharge}</span> : null}
                              {action.used ? <span className="vi-lair-used">USED</span> : null}
                            </div>
                            <div className="vi-lair-desc">{action.description}</div>
                            <button
                              type="button"
                              onClick={(function(ai) { return function() { onUse(ai); }; })(i)}
                              disabled={!!action.used}
                              className={'vi-btn vi-btn-sm ' + (action.used ? 'vi-btn-secondary' : 'vi-btn-primary')}
                            >
                              {'\u26a1 Use'}
                            </button>
                          </div>
                          <button type="button" onClick={(function(ai) { return function() { onRemove(ai); }; })(i)} className="vi-btn vi-btn-secondary vi-btn-sm">Remove</button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <button type="button" onClick={onAdd} className="btn-primary-gold vi-btn vi-btn-secondary">+ Add Lair Action</button>

              <div className="vi-environment">
                <h4>🌍 Environment Conditions</h4>
                <div className="vi-environment-grid">
                  {ENVIRONMENT_OPTIONS.map(function(opt) {
                    return (
                      <label className="vi-env-label" key={opt.key}>
                        <input
                          type="checkbox"
                          checked={!!environment[opt.key]}
                          onChange={(function(k) { return function(e) { onToggleEnvironment(k, e.target.checked); }; })(opt.key)}
                        />
                        <span>{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
                <div id={'environment-effects-' + index} className="vi-hint vi-mt-10">
                  {activeEffects.map(function(opt, k) {
                    return (
                      <React.Fragment key={opt.key}>
                        {k > 0 ? <br /> : null}
                        {opt.effect}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LairView;
